import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Check, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WaitlistForm({ title, subtitle, tag, className = "" }) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await axios.post(`${BACKEND_URL}/api/waitlist`, {
        ...formData,
        formType: tag,
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error("Waitlist submission error:", err);
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      city: "",
    });
    setError(null);
  };

  return (
    <div id="waitlist" className={`bg-lws-navy text-lws-paper p-12 md:p-20 text-center ${className}`} data-testid="waitlist-section">
      <div className="max-w-2xl mx-auto space-y-8">
        <AnimatePresence mode="wait">
          {!isSubmitted ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-8"
            >
              <h2 className="text-4xl md:text-6xl font-serif leading-tight">{title}</h2>
              <p className="text-lg text-lws-paper/70 font-serif leading-relaxed">{subtitle}</p>
              
              <form onSubmit={handleSubmit} className="space-y-4 pt-6" data-testid="waitlist-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder="First Name" 
                    className="bg-white/10 border border-white/20 px-6 py-4 text-lws-paper placeholder:text-lws-paper/30 focus:outline-none focus:border-lws-amber transition-colors"
                    data-testid="waitlist-firstname"
                  />
                  <input 
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last Name" 
                    className="bg-white/10 border border-white/20 px-6 py-4 text-lws-paper placeholder:text-lws-paper/30 focus:outline-none focus:border-lws-amber transition-colors"
                    data-testid="waitlist-lastname"
                  />
                </div>
                <input 
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Email Address" 
                  className="w-full bg-white/10 border border-white/20 px-6 py-4 text-lws-paper placeholder:text-lws-paper/30 focus:outline-none focus:border-lws-amber transition-colors"
                  data-testid="waitlist-email"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone (optional)" 
                    className="bg-white/10 border border-white/20 px-6 py-4 text-lws-paper placeholder:text-lws-paper/30 focus:outline-none focus:border-lws-amber transition-colors"
                    data-testid="waitlist-phone"
                  />
                  <input 
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City / Location" 
                    className="bg-white/10 border border-white/20 px-6 py-4 text-lws-paper placeholder:text-lws-paper/30 focus:outline-none focus:border-lws-amber transition-colors"
                    data-testid="waitlist-city"
                  />
                </div>

                {error && (
                  <p className="text-lws-rose text-sm" data-testid="waitlist-error">{error}</p>
                )}

                <button 
                  type="submit"
                  disabled={isLoading}
                  className="w-full md:w-auto bg-lws-amber text-lws-paper px-10 py-4 text-xs uppercase tracking-widest font-bold hover:bg-lws-rose transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mx-auto"
                  data-testid="waitlist-submit"
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Join the Waitlist"
                  )}
                </button>
              </form>
              
              <p className="text-[10px] uppercase tracking-[0.3em] text-lws-paper/30">
                Tag: lws_{tag}_interest • Privacy First
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-12 space-y-6"
              data-testid="waitlist-success"
            >
              <div className="w-20 h-20 bg-lws-green rounded-full flex items-center justify-center mx-auto mb-8">
                <Check className="text-lws-paper" size={40} />
              </div>
              <h2 className="text-4xl md:text-5xl font-serif leading-tight">You're on the list.</h2>
              <p className="text-lg text-lws-paper/70 font-serif leading-relaxed">
                Thank you for your interest in the Society. We will reach out as soon as a space opens for you.
              </p>
              <button 
                onClick={resetForm}
                className="text-[10px] uppercase tracking-[0.3em] text-lws-amber hover:text-lws-paper transition-colors pt-8"
              >
                Register another email
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
